// combinator-panel.ts
// Binds a panel's data-cmb-* elements to a Combinator. docs/reference/

import Base from '../base/base';
import { isAsset } from '../../shared/utils/elevation-catalog';
import { Combinator, Config, Selection, Snapshot, createCombinator } from './combinator';
import './combinator-panel.scss';

export interface CombinatorPanelOptions {
  selector?: string;
  root?: HTMLElement;
  config: Config;
  initial?: Selection;
  /** Pulse the selected options until the first click. Default true. */
  attract?: boolean;
  debug?: boolean;
}

interface OptionBinding {
  element: HTMLElement;
  layer: string;
  option: string;
  known: boolean;
}

interface MatchBinding {
  element: HTMLElement;
  tokens: Array<{ layer: string; option: string }>;
}

export const OPTION_ATTRIBUTE = 'data-cmb-option';
export const OUT_ATTRIBUTE = 'data-cmb-out';
export const MATCH_ATTRIBUTE = 'data-cmb-match';
export const STATE_ATTRIBUTE = 'data-cmb-state';
export const CHANGE_EVENT = 'cmb:change';
export const READY_EVENT = 'cmb:ready';

/** `plan-the-coupe` splits at the first hyphen into layer and option. */
export function parseOption(value: string): { layer: string; option: string } | null {
  const at = value.indexOf('-');

  if (at < 1 || at === value.length - 1) return null;

  return { layer: value.slice(0, at), option: value.slice(at + 1) };
}

/** Remove and re-add a marker attribute, so a CSS animation replays. */
function flash(element: HTMLElement, attribute: string): void {
  element.removeAttribute(attribute);
  void element.offsetWidth;
  element.setAttribute(attribute, '');
}

export default class CombinatorPanel extends Base {
  readonly root: HTMLElement | null;
  readonly core: Combinator;

  private options: OptionBinding[] = [];
  private outs: HTMLElement[] = [];
  private matches: MatchBinding[] = [];
  private attract: boolean;
  private unsubscribe: (() => void) | null = null;
  private boundOnClick: (event: Event) => void;
  private started = false;

  constructor(options: CombinatorPanelOptions) {
    super(options.debug || false);

    this.root = options.root || (options.selector ? document.querySelector<HTMLElement>(options.selector) : null);
    this.core = createCombinator(options.config, options.initial);
    this.attract = options.attract !== false;
    this.boundOnClick = this.onClick.bind(this);

    this.init();
  }

  get started$(): boolean {
    return this.started;
  }

  snapshot(): Snapshot {
    return this.core.snapshot();
  }

  select(layer: string, option: string): Snapshot {
    return this.core.select(layer, option);
  }

  private init(): void {
    if (!this.root) {
      this.log('CombinatorPanel: root not found');
      return;
    }

    if (!this.cacheDOM()) return;

    this.root.addEventListener('click', this.boundOnClick);
    this.unsubscribe = this.core.subscribe((snapshot) => this.paint(snapshot));

    if (this.attract) this.root.classList.add('cmb-attract');

    this.root.setAttribute('data-cmb-ready', '');
    this.started = true;
    this.paint(this.core.snapshot());
    this.root.dispatchEvent(new CustomEvent(READY_EVENT, { detail: this.core.snapshot(), bubbles: true }));
    this.log('CombinatorPanel started', this.options.length, 'options', this.outs.length, 'outs');
  }

  /** False when the panel refuses to start: two options share one value. */
  private cacheDOM(): boolean {
    const root = this.root!;
    const seen = new Set<string>();
    const layers = new Map(this.core.config.layers.map((layer) => [layer.id, layer]));
    let duplicate: string | null = null;

    this.options = Array.from(root.querySelectorAll<HTMLElement>(`[${OPTION_ATTRIBUTE}]`))
      .map((element) => {
        const value = element.getAttribute(OPTION_ATTRIBUTE) || '';
        const parsed = parseOption(value);

        if (seen.has(value)) duplicate = value;
        seen.add(value);

        if (!parsed) {
          this.log('CombinatorPanel: option value has no layer', value);
          element.setAttribute(STATE_ATTRIBUTE, 'unknown');
          return null;
        }

        const layer = layers.get(parsed.layer);
        const known = Boolean(layer) && layer!.options.includes(parsed.option);

        if (!known) {
          this.log('CombinatorPanel: option the config does not know', value);
          element.setAttribute(STATE_ATTRIBUTE, 'unknown');
        }

        return { element, layer: parsed.layer, option: parsed.option, known };
      })
      .filter((entry): entry is OptionBinding => entry !== null);

    if (duplicate) {
      console.error(`CombinatorPanel: two options share the value "${duplicate}"; the panel did not start.`);
      root.setAttribute('data-cmb-error', 'duplicate-option');
      return false;
    }

    this.outs = Array.from(root.querySelectorAll<HTMLElement>(`[${OUT_ATTRIBUTE}]`));
    this.matches = Array.from(root.querySelectorAll<HTMLElement>(`[${MATCH_ATTRIBUTE}]`)).map((element) => ({
      element,
      tokens: (element.getAttribute(MATCH_ATTRIBUTE) || '')
        .split(/\s+/)
        .map(parseOption)
        .filter((token): token is { layer: string; option: string } => token !== null)
    }));

    return true;
  }

  private onClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const element = target ? target.closest<HTMLElement>(`[${OPTION_ATTRIBUTE}]`) : null;

    if (!element || !this.root!.contains(element)) return;

    event.preventDefault();

    const binding = this.options.find((entry) => entry.element === element);

    if (!binding || !binding.known) return;
    if (element.getAttribute(STATE_ATTRIBUTE) === 'unavailable') return;

    if (this.attract) {
      this.root!.classList.remove('cmb-attract');
      this.attract = false;
    }

    this.core.select(binding.layer, binding.option);
  }

  private paint(snapshot: Snapshot): void {
    const root = this.root!;

    this.options.forEach(({ element, layer, option, known }) => {
      if (!known) return;

      const selected = snapshot.selection[layer] === option;
      const available = (snapshot.available[layer] || []).includes(option);
      const state = selected ? 'selected' : available ? 'available' : 'unavailable';

      if (element.getAttribute(STATE_ATTRIBUTE) !== state) element.setAttribute(STATE_ATTRIBUTE, state);
      element.setAttribute('aria-pressed', selected ? 'true' : 'false');
      element.setAttribute('aria-disabled', available ? 'false' : 'true');
    });

    const tokens = Object.keys(snapshot.selection)
      .filter((layer) => snapshot.selection[layer] !== null)
      .map((layer) => `${layer}-${snapshot.selection[layer]}`);

    root.setAttribute('data-cmb-selection', tokens.join(' '));
    root.setAttribute('data-cmb-outcome', snapshot.outcome ? 'true' : 'false');

    this.matches.forEach(({ element, tokens: wanted }) => {
      const active = wanted.every((token) => snapshot.selection[token.layer] === token.option);

      element.setAttribute(STATE_ATTRIBUTE, active ? 'active' : 'inactive');
    });

    if (snapshot.outcome) this.outs.forEach((element) => this.write(element, snapshot.outcome!));

    root.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: snapshot, bubbles: true }));
  }

  /** One out element takes one key of the outcome state. */
  private write(element: HTMLElement, state: Record<string, unknown>): void {
    const key = element.getAttribute(OUT_ATTRIBUTE) || '';
    const value = state[key];
    const asImage = element instanceof HTMLImageElement;
    const asBackground = element.getAttribute('data-cmb-out-as') === 'background';

    if (asImage) {
      const url = isAsset(value) ? value.url : typeof value === 'string' ? value : null;
      const alt = isAsset(value) && typeof value.alt === 'string' ? value.alt : null;

      if (url) this.swapImage(element, url, alt);
      else element.setAttribute('data-cmb-empty', '');
      return;
    }

    if (asBackground) {
      const url = isAsset(value) ? value.url : typeof value === 'string' ? value : null;

      if (url && element.style.backgroundImage !== `url("${url}")`) {
        element.style.backgroundImage = `url("${url}")`;
        flash(element, 'data-cmb-swapped');
      }
      return;
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      const next = String(value);

      if (element.textContent !== next) {
        element.textContent = next;
        flash(element, 'data-cmb-updated');
      }
      return;
    }

    if (value == null) {
      if (element.textContent !== '') {
        element.textContent = '';
        flash(element, 'data-cmb-updated');
      }
    }
  }

  /** Load the next image first, then swap, so the panel never shows a blank. */
  private swapImage(image: HTMLImageElement, url: string, alt: string | null): void {
    if (image.getAttribute('data-cmb-src') === url) return;

    image.setAttribute('data-cmb-src', url);
    image.setAttribute('data-cmb-loading', '');

    const probe = new Image();
    const apply = (): void => {
      if (image.getAttribute('data-cmb-src') !== url) return;

      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = url;
      if (alt !== null) image.alt = alt;
      image.removeAttribute('data-cmb-loading');
      flash(image, 'data-cmb-swapped');
    };

    probe.onload = apply;
    probe.onerror = apply;
    probe.src = url;
  }

  destroy(): void {
    if (this.root) {
      this.root.removeEventListener('click', this.boundOnClick);
      this.root.removeAttribute('data-cmb-ready');
      this.root.classList.remove('cmb-attract');
    }

    if (this.unsubscribe) this.unsubscribe();
    this.unsubscribe = null;
    this.started = false;
  }
}
